import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { TemplatesQueries } from 'src/app/shared/types/get-query';
import { OrderTypeEnum } from '../../../models/new/order';
import { ShotType } from '../../../models/new/shot';
import { CreateShotTypeInput, LegacyCodeOption, UpdateShotTypeInput } from '../../../models/types/shot-type-input.type';

export type ShotTypeModalData = {
	shotType?: ShotType;
};

@Component({
	standalone: false,
	selector: 'bt-shot-type-modal',
	templateUrl: './shot-type-modal.component.html',
	styleUrls: ['./shot-type-modal.component.scss']
})
export class ShotTypeModalComponent implements OnInit {
	readonly #dialogRef = inject(MatDialogRef<ShotTypeModalComponent>);
	readonly #graphQlService = inject(GraphQLService);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #destroyRef = inject(DestroyRef);
	readonly data = inject<ShotTypeModalData | undefined>(MAT_DIALOG_DATA, { optional: true });

	readonly isEdit = !!this.data?.shotType?.id;
	// Il backend non permette di rinominare uno shot type gia' usato da ordini, export o styles
	readonly nameLocked = this.isEdit && !!this.data?.shotType?.isInUse;

	// Uno shot type appartiene solo a Still-Life o On Model
	readonly orderTypes = [
		{ id: OrderTypeEnum.StillLife, name: 'Still-Life' },
		{ id: OrderTypeEnum.Dressed, name: 'On Model' }
	];

	name = this.data?.shotType?.name ?? '';
	orderTypeId: OrderTypeEnum | null = this.data?.shotType?.orderType ?? null;
	legacyCode: string | null = null;
	legacyCodes: LegacyCodeOption[] = [];
	loading = false;
	validation = {
		name: true,
		orderType: true
	};

	ngOnInit(): void {
		if (!this.isEdit) {
			this.loadLegacyCodes();
		}
	}

	loadLegacyCodes(): void {
		this.loading = true;
		this.#graphQlService
			.getData(TemplatesQueries.GET_FREE_LEGACY_CODES_FOR_SHOT_TYPE)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: any) => {
					this.legacyCodes = res?.data?.freeLegacyCodesForShotType ?? [];
					this.loading = false;
				},
				error: (err) => {
					this.#utilitiesService.showSnackbar('Error fetching legacy codes', SnackbarType.Error);
					this.loading = false;
					console.error(err);
				}
			});
	}

	onNameChange(): void {
		this.validation.name = true;
	}

	onOrderTypeChange(): void {
		this.validation.orderType = true;
	}

	close(): void {
		this.#dialogRef.close();
	}

	apply(): void {
		this.validation.name = !!this.name.trim();
		this.validation.orderType = this.orderTypeId !== null;

		if (!this.validation.name || !this.validation.orderType) {
			return;
		}

		if (this.isEdit) {
			const input: UpdateShotTypeInput = {
				id: this.data!.shotType!.id!,
				name: this.name.trim(),
				orderTypeId: this.orderTypeId!
			};
			this.#dialogRef.close(input);
			return;
		}

		const input: CreateShotTypeInput = {
			name: this.name.trim(),
			orderTypeId: this.orderTypeId!,
			legacyCode: this.legacyCode ?? undefined
		};
		this.#dialogRef.close(input);
	}
}
